/**
 * PLT-4622 Phase 2 — `useLiveJobStatus` host-side hook.
 *
 * Companion to the in-iframe React/state-driven MCP-app card. The card
 * never opens an SSE connection itself — iframes loaded with permissive
 * CSPs and JWT-in-query-param auth can't safely manage long-lived event
 * streams. Instead, the chat layout mounts this hook *next to* the
 * `<McpAppIframe>` and pushes deltas into the iframe via the
 * `postLiveUpdate` imperative handle.
 *
 * Usage (PLT-4622.2.6):
 *
 *   const iframeRef = useRef<McpAppIframeHandle>(null);
 *   useLiveJobStatus({
 *     jobId,
 *     initialPayload: toolResult,
 *     onUpdate: (payload) => iframeRef.current?.postLiveUpdate(payload),
 *   });
 *   <McpAppIframe ref={iframeRef} ... />
 *
 * The hook handles:
 *   - capability check (no-op when no `jobId` is present — Claude clients
 *     receive payloads without the field, so the hook simply doesn't run)
 *   - reload reconciliation (PLT-4622.2.7): on mount, fetch
 *     `/api/chat/jobs/{jobId}/snapshot` BEFORE opening the SSE so the card
 *     converges to truth even if the SSE pipe drops the terminal event
 *   - SSE subscription with EventSource (auth via cookie / same-origin)
 *   - clean teardown on unmount or terminal `done` event
 *
 * The hook is intentionally not coupled to the CompanyCard payload shape —
 * it just merges `{ status, kpis?, errorMessage?, jobId }` into the
 * payload and forwards it. Any future card (DMA freemium, group reports,
 * etc.) can use the same hook by giving it an `initialPayload` and
 * absorbing the deltas through its own state machine.
 */
import { useEffect } from 'react';

/** Wire shape of an SSE event from `/api/chat/jobs/{jobId}`. */
export interface LiveJobEvent {
  type: 'subscribed' | 'snapshot' | 'progress' | 'done' | 'timeout';
  jobId?: number;
  status?: string;
  detail?: Record<string, unknown>;
}

export interface UseLiveJobStatusOptions {
  /** Job id from the tool result. When `undefined`, the hook is a no-op. */
  jobId: number | undefined;
  /** The original tool result payload — extended with each SSE event. */
  initialPayload: unknown;
  /** Called whenever a fresh, merged payload is ready to push to the iframe. */
  onUpdate: (payload: unknown) => void;
  /**
   * Override the snapshot/SSE base URL. Defaults to same-origin
   * `/api/chat/jobs/{jobId}`. Useful for unit tests.
   */
  baseUrl?: string;
}

/**
 * Lifecycle states from which no further transition is expected. Once the
 * snapshot/stream reports one of these, the card has reached its final form
 * and we stop both the SSE subscription and the snapshot backstop poll.
 * Mirrors the terminal states of the backend `DD_LIFECYCLE_STATUSES`.
 */
const TERMINAL_STATUSES = new Set(['ready', 'error']);

function isTerminalStatus(status: string | undefined): boolean {
  return status !== undefined && TERMINAL_STATUSES.has(status);
}

/**
 * How often the snapshot backstop re-polls while the job is still in flight,
 * and the longest we keep polling before giving up. The snapshot endpoint
 * reads job state straight from the DB, so the poll converges the card to
 * truth even when the live SSE event is never delivered (e.g. the worker and
 * the SSE handler are wired to different event-bus modes). The live SSE path
 * is still the fast path; this is purely the "never get stuck" floor.
 */
const SNAPSHOT_POLL_INTERVAL_MS = 12_000;
const SNAPSHOT_POLL_MAX_DURATION_MS = 20 * 60_000;

/** Merge an SSE event onto an existing payload. */
export function mergeLiveEventIntoPayload(payload: unknown, event: LiveJobEvent): unknown {
  if (typeof payload !== 'object' || payload === null) return payload;
  const next: Record<string, unknown> = { ...(payload as Record<string, unknown>) };
  if (event.status) next.status = event.status;
  if (event.detail && typeof event.detail === 'object') {
    // Spread detail into the top level — for the CompanyCard this lets the
    // worker emit `{ kpis: {...} }` on terminal `ready` and have it
    // surface without the iframe having to know the shape ahead of time.
    Object.assign(next, event.detail);
  }
  return next;
}

export function useLiveJobStatus(options: UseLiveJobStatusOptions): void {
  const { jobId, initialPayload, onUpdate, baseUrl } = options;

  useEffect(() => {
    if (typeof jobId !== 'number' || !Number.isFinite(jobId)) {
      // No jobId in the tool result → either Phase 1 hand-off card, a
      // non-DD tool, or an MCP host that doesn't pass the field through.
      // Either way, nothing to subscribe to.
      return;
    }
    if (typeof window === 'undefined' || typeof window.EventSource !== 'function') {
      // Test/SSR environments — no-op; the static payload still renders.
      return;
    }

    const root = baseUrl ?? `/api/chat/jobs/${jobId}`;
    let cancelled = false;
    // Mutable working copy so consecutive events compose (e.g. progress
    // sets `status: 'computing'`, terminal sets `status: 'ready', kpis: {...}`).
    let working: unknown = initialPayload;
    let pollTimer: ReturnType<typeof setInterval> | undefined;

    const stopPolling = () => {
      if (pollTimer !== undefined) {
        clearInterval(pollTimer);
        pollTimer = undefined;
      }
    };

    // Fetch the DB-backed snapshot and merge it in. Returns the merged
    // status so callers can stop the backstop poll once the job is terminal.
    const applySnapshot = async (): Promise<string | undefined> => {
      const response = await fetch(`${root}/snapshot`, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`snapshot ${response.status}`);
      const snapshot = (await response.json()) as LiveJobEvent;
      if (cancelled) return undefined;
      working = mergeLiveEventIntoPayload(working, { ...snapshot, type: 'snapshot' });
      onUpdate(working);
      return snapshot.status;
    };

    // 1) Reload reconciliation — fetch the current snapshot before opening
    //    the SSE. Avoids relying on a frozen "computing" tool-result after
    //    a tab reload or scrollback render.
    applySnapshot()
      .then(status => {
        // If the job is already terminal at mount (card rendered from
        // scrollback after completion), there is nothing live to wait for.
        if (status !== undefined && isTerminalStatus(status)) stopPolling();
      })
      .catch(err => {
        // Snapshot is best-effort: if it fails, the SSE or the next poll
        // tick will still converge the card. Logging is sufficient.
        // eslint-disable-next-line no-console
        console.warn('[useLiveJobStatus] snapshot fetch failed', err);
      });

    // 2) Snapshot backstop. The live SSE path below is the fast path, but it
    //    is fragile: a single dropped/undelivered bus event (e.g. the worker
    //    publishing on a different event-bus mode than the SSE handler
    //    consumes) would otherwise strand the card on "computing" forever,
    //    with no way to recover short of a reload. Re-polling the DB-backed
    //    snapshot on an interval makes the card self-heal regardless of the
    //    bus, bounded so we never poll a forgotten tab indefinitely. Cleared
    //    as soon as any path (poll or SSE) sees a terminal state.
    const pollStartedAt = Date.now();
    pollTimer = setInterval(() => {
      if (cancelled) return;
      if (Date.now() - pollStartedAt > SNAPSHOT_POLL_MAX_DURATION_MS) {
        stopPolling();
        return;
      }
      applySnapshot()
        .then(status => {
          if (status !== undefined && isTerminalStatus(status)) stopPolling();
        })
        .catch(() => {
          // Transient — keep polling; the next tick may succeed.
        });
    }, SNAPSHOT_POLL_INTERVAL_MS);

    // 3) Open the SSE subscription. EventSource includes credentials by
    //    default for same-origin requests, so the existing chat session
    //    cookie is enough — no JWT-in-query trick is required at this
    //    layer (the SSE endpoint runs on the chat plugin's auth strategy).
    let source: EventSource;
    try {
      source = new EventSource(root);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useLiveJobStatus] EventSource construction failed', e);
      // Leave the snapshot backstop running — it is now the only path to a
      // live transition, and the cleanup below still clears it.
      return () => {
        cancelled = true;
        stopPolling();
      };
    }

    source.onmessage = (msg: MessageEvent<string>) => {
      if (cancelled) return;
      let event: LiveJobEvent;
      try {
        event = JSON.parse(msg.data) as LiveJobEvent;
      } catch {
        return;
      }
      // 'subscribed' is just a primer — nothing to merge.
      if (event.type === 'subscribed') return;
      working = mergeLiveEventIntoPayload(working, event);
      onUpdate(working);
      // Terminal via the live path — stop both the stream and the backstop.
      if (isTerminalStatus(event.status)) {
        stopPolling();
        source.close();
      } else if (event.type === 'done' || event.type === 'timeout') {
        // The job's stream is over, but not what the card shows. After a generation succeeds, the
        // compute runs as another job, started by the user's approval or, with no review step, by the
        // generation itself, and the snapshot follows it (PLT-7448, PLT-7449). So only the stream
        // closes: the poll runs on until it reads a terminal status or reaches its cap.
        source.close();
      }
    };

    source.onerror = () => {
      // EventSource auto-reconnects with built-in backoff; we let it. If
      // the server returns a terminal error response (e.g. 403), the
      // reconnect will keep failing — that's acceptable: the snapshot
      // backstop poll still converges the card, and the static payload
      // remains visible meanwhile.
    };

    return () => {
      cancelled = true;
      stopPolling();
      source.close();
    };
    // We intentionally don't include `initialPayload` in the dependency
    // array: the host updates `working` via merged deltas, and a new
    // initialPayload from Phase-1 polling would reset progress mid-stream.
    // Hook unmount + remount on jobId change is the right way to reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, baseUrl]);
}
